import React, { useCallback, useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { type RenderSuggestionsContainerParams } from 'react-autosuggest';

import { useAutoSuggest } from '@uncinc/react-searchkit';

import { createSearchUrl } from '../../../helpers/createSearchUrl';
import SearchAutosuggest, { type SearchAutosuggestProps } from '../../Search/Autosuggest';
import SearchAllContentAutosuggestHit from '../AutosuggestHit';
import type { SearchAllContentHit } from '../types';
import {
  APP_OPTIONS,
  FAQ_BASE_PATH,
  NODE_URL_ALIAS_TEAM,
  SEARCH_PATH,
} from '../../../config/constants';

interface SuggestionsContainerProps {
  params: RenderSuggestionsContainerParams;
  onSearchSubmit: (query: string) => void;
}

const SuggestionsContainer: React.FC<SuggestionsContainerProps> = ({ params, onSearchSubmit }) => {
  const { children, containerProps, query } = params;

  const { t } = useTranslation();

  const isOpen = containerProps.className?.includes('react-autosuggest__suggestions-container--open') ?? false;

  return (
    <div {...containerProps}>
      {children}
      <button
        type="button"
        className="SearchAllContent__SearchBar__show-all-button"
        // We need to conditionally change disabled depending on if the container is open or not,
        // because otherwise this button will be focusable if not visible and prevent the focus
        // trap in the menu from working correctly.
        disabled={!isOpen}
        // using onMouseDown instead of onClick
        // to make sure this gets called before react-searchkit onBlur takes over
        // otherwise things break :/
        onMouseDown={(e) => {
          onSearchSubmit(query || '');
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {t('components.search_all_content.search_bar.show_all_results')}
      </button>
    </div>
  );
};

export interface SearchAllContentSearchAutosuggestProps extends Partial<SearchAutosuggestProps> {
  searchInputRef?: React.RefObject<HTMLInputElement>;
}

const SearchAllContentSearchAutosuggest: React.FC<SearchAllContentSearchAutosuggestProps> = (
  props,
) => {
  const [selectionMade, setSelectionMade] = useState<boolean>(false);
  const activeElement = document.activeElement as HTMLInputElement | undefined;
  const { t } = useTranslation();
  const history = useHistory();
  const { clearSearch } = useAutoSuggest();

  const {
    searchbarProps,
    autosuggestProps,
    searchInputRef,
  } = props;

  const classNames = c('SearchAllContent__SearchBar');

  const onSearchSubmit = useCallback((query: string) => {
    if (selectionMade) {
      setSelectionMade(false);
      return;
    }

    const url = createSearchUrl({
      pathname: SEARCH_PATH,
      index: APP_OPTIONS.parameters.searchkitIndexAllContent,
      value: query,
    });

    try {
      history.push(url);
      activeElement?.blur();
      clearSearch();
    } catch (error) {
      console.error(error);
    }
  }, [activeElement, clearSearch, history, selectionMade]);

  return (
    <SearchAutosuggest
      {...props}
      searchInputRef={searchInputRef}
      autosuggestProps={{
        ...autosuggestProps,
        getSuggestionValue: (hit: SearchAllContentHit) => (
          // Page_title for nodes, name for taxonomy terms, colleague_full_name for profiles.
          hit.page_title?.[0] || hit.name?.[0] || hit.colleague_full_name?.[0]
        ) ?? '',
        renderSuggestion: (hit: SearchAllContentHit) => (
          <SearchAllContentAutosuggestHit hit={hit} />
        ),
        renderSuggestionsContainer: (containerProps) => (
          <SuggestionsContainer params={containerProps} onSearchSubmit={onSearchSubmit} />
        ),
        onSuggestionSelected: (e, { suggestion }: { suggestion: SearchAllContentHit }) => {
          setSelectionMade(true);

          if (suggestion.type?.[0] !== 'faq') {
            let url = suggestion.url[0];
            if (suggestion.objectID.includes('entity:profile')) {
              url = createSearchUrl({
                pathname: suggestion.colleague_team_url?.[0] || NODE_URL_ALIAS_TEAM,
                index: APP_OPTIONS.parameters.searchkitIndexTeam,
                value: suggestion.colleague_full_name?.[0] || '',
              });
            }
            history.push(url);
            activeElement?.blur();
            return;
          }

          // All the FAQs are on the FAQ page. Each FAQ has a unique id attached.
          // We can use this to navigate to the FAQ page and open the FAQ accordion
          // that belongs to the id.
          const link = `${FAQ_BASE_PATH}#${suggestion.uuid[0]}`;
          history.replace(link);

          activeElement?.blur();
        },
      }}
      searchbarProps={{
        ...searchbarProps,
        onSubmit: onSearchSubmit,
        className: classNames,
        placeholder: t('components.search_all_content.search_bar.placeholder'),
        buttonText: t('buttons.search'),
      }}
    />
  );
};

export default SearchAllContentSearchAutosuggest;
